// SPDX-License-Identifier: AGPL-3.0-or-later

import ShopCatalog from '@app/features/shop/state/ShopCatalog';
import {SHOP_REGION_CLOSED_DESCRIPTOR} from '@app/features/shop/utils/ShopMessageDescriptors';
import type {ShopViewerPayment} from '@fluxer/schema/src/domains/shop/ShopPaymentSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

export const SHOP_CURRENCY_NOTE_DESCRIPTOR = msg({
	message:
		"Micup is an Azerbaijani company, so payments are collected in Azerbaijani manat (AZN). TL amounts are informational, based on the Central Bank of Azerbaijan's daily exchange rate.",
	comment:
		'Payment currency explanation on the shop, Micup Plus page, checkout and FAQ. Keep the company location explicit.',
});
export const SHOP_BANK_NOTE_DESCRIPTOR = msg({
	message:
		'The TL amount on your card statement may differ slightly: your bank converts AZN at its own exchange rate and may charge a foreign transaction fee.',
	comment:
		'Card statement disclaimer after the AZN payment explanation on the shop, Micup Plus page, checkout and FAQ.',
});
const PADDLE_USD_NOTE_DESCRIPTOR = msg({
	message:
		'Payments are processed by Paddle.com, our reseller and Merchant of Record, in US dollars (USD). Taxes are included in the price.',
	comment:
		'Payment explanation on the shop, Micup Plus page, checkout and FAQ where Paddle sells in US dollars. Keep "Paddle.com" and "Merchant of Record": Paddle has to be named as the Merchant of Record.',
});
const PADDLE_TRY_NOTE_DESCRIPTOR = msg({
	message:
		'Payments are processed by Paddle.com, our reseller and Merchant of Record, in Turkish lira (TL). Taxes are included in the price.',
	comment:
		'Payment explanation on the shop, Micup Plus page, checkout and FAQ where Paddle sells in Turkish lira. Keep "Paddle.com" and "Merchant of Record": Paddle has to be named as the Merchant of Record.',
});

interface ShopCurrencyNoteProps {
	// The checkout passes how its quote is charged; elsewhere the catalog's route for this viewer.
	payment?: ShopViewerPayment | null;
}

export const ShopCurrencyNote = observer(({payment}: ShopCurrencyNoteProps) => {
	const {i18n} = useLingui();
	const route = payment === undefined ? ShopCatalog.payment : payment;
	if (route === null) {
		return <span data-flx="shop.shop-currency-note.note">{i18n._(SHOP_REGION_CLOSED_DESCRIPTOR)}</span>;
	}
	if (route?.provider === 'paddle') {
		return (
			<span data-flx="shop.shop-currency-note.note">
				{i18n._(route.currency === 'USD' ? PADDLE_USD_NOTE_DESCRIPTOR : PADDLE_TRY_NOTE_DESCRIPTOR)}
			</span>
		);
	}
	return (
		<span data-flx="shop.shop-currency-note.note">
			{i18n._(SHOP_CURRENCY_NOTE_DESCRIPTOR)} {i18n._(SHOP_BANK_NOTE_DESCRIPTOR)}
		</span>
	);
});
